/**
 * triggerAnalyticsPrefetch — fire-and-forget warm of the analytics
 * dashboard right after login (perf #75).
 *
 * The backend wraps GET /profile/analytics in a 5-minute
 * stale-while-revalidate cache (the 11+ aggregation queries cost
 * ~800ms-2s cold). Firing this immediately after login populates that
 * cache while the student is still on the landing screen, so the first
 * real open of the Progress page returns the cached payload in <5ms
 * instead of paying the cold-compute cost.
 *
 * Mirrors triggerLoginPrewarm.js exactly:
 *   - Resolves to a result object; NEVER rejects (a perf optimization,
 *     not a feature gate — failures are swallowed + console.debug'd).
 *   - 5s timeout to bound the call.
 *   - Tagged `x-background-request: true` so BOTH the global 401
 *     interceptor (no auto-logout on a stale-token prefetch race) AND
 *     the COPPA age/consent interceptor (no DOB modal / consent overlay
 *     popped in the user's face) skip it. See
 *     coppa/ageConsentInterceptor.js `isBackground`.
 *
 * Usage (from App.js post-login flow):
 *
 *   triggerAnalyticsPrefetch(accessToken);   // fire-and-forget
 */

import axios from 'axios';
import { API_URL } from '../config';

export async function triggerAnalyticsPrefetch(accessToken) {
  if (!accessToken) {
    return { skipped: true, reason: 'no-token' };
  }
  try {
    await axios.get(`${API_URL}/api/v1/profile/analytics`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'x-background-request': 'true',
      },
      // The endpoint returns ~immediately on a cache hit and ~1-2s on a
      // cold compute; 5s bounds the worst case. We don't use the body —
      // the point is to populate the server-side SWR cache.
      timeout: 5000,
    });
    return { skipped: false };
  } catch (err) {
    // Quiet failure mode — never surface to the user.
    // eslint-disable-next-line no-console
    console.debug('analytics prefetch skipped:', err?.message || err);
    return { skipped: true, reason: 'network-or-server-error' };
  }
}

export default triggerAnalyticsPrefetch;
